/**
 * @prettier
 * @flow
 * */

import { RenderMode } from '@site-builder/common/src/types/build';
import { AuthorizationTypes as AuthorizationType } from '@site-builder/common/src/types/model/landing';
import { isUserAccountShown } from '@site-builder/common/src/utils/login';
import React from 'react';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import { IntlProvider } from 'react-intl';

import type { ScriptCommonArgs } from '../types';
import type { HeaderBlock } from '@site-builder/common/src/flow-types/blocks';

import { Header } from '../../components/Blocks/Header';
import { CartButton } from '../../components/Blocks/Header/CartButton';
import * as HeaderDataId from '../../components/Blocks/Header/dataIds';
import {
  HeaderLayout,
  LayoutEnum,
} from '../../components/Blocks/Header/layout';
import { UserAccount } from '../../components/Blocks/Header/UserAccount';
import { userIdModalDataid } from '../../components/ModalWindows/EditorModals';
import { isCustomAuth } from '../../experiments/partnersRequests/utils';
import {
  LocaleSelect,
  LocaleSelectType,
} from '../../ui-components/Editor/LocaleSelect';
import { sendClick, XAEvents } from '../../utils/init-xsolla-analytics';
import { isMobileScreen } from '../../utils/mobile';
import { defaultLocale } from '../../utils/translation';
import { analyticsManager } from '../afterBlocks/analytics/analytics';
import { AnalyticsActions } from '../afterBlocks/analytics/analyticsActions';
import { AnalyticsCategories } from '../afterBlocks/analytics/analyticsCategories';
import { lazyLoadImage } from '../beforeBlocks/lazyLoad';
import events from '../events';
import { getTokenFromCookie } from '../helpers/common';
import { initBuyButtonsInBlock } from '../helpers/cta';
import { getStore } from '../store';
import { openModalWindow } from '../ui-components/editor/modalWindow';
import { GlobalUniversalCart } from './store/cart/GlobalUniversalCart';
import { UniversalCart } from './store/cart/UniversalCart';
import { toggleCartEmail } from './store/cartEmail';
import { initCartModalOnce, openCartModal } from './store/cartModal';
import { StoreAPIDataService } from './store/services/StoreAPIDataService';
import { openLogin, LOGIN_SOURCES } from './user-account-services';
import { setLastLoginSource } from './user-account-services/utils';

const handleOpenCart = (cart: UniversalCart) => () => {
  const cartButton = document.querySelector('.header-cart-button');
  const cartModal = document.querySelector('.ui-site-cart-modal');
  if (cartButton) {
    analyticsManager.sendEvent({
      category: AnalyticsCategories.HEADER,
      event: AnalyticsActions.CLICK_CART,
      page: `landing_header_${AnalyticsActions.CLICK_CART}`,
    });
  }
  toggleCartEmail(cartModal, cart);
  openModalWindow(cartModal, 'Cart');
  analyticsManager.sendEvent({
    category: AnalyticsCategories.HEADER,
    event: AnalyticsActions.OPEN_CART,
    page: `landing_${AnalyticsActions.OPEN_CART}`,
  });
};

const getMountNodeByDataId = (blockId, dataId) =>
  document.querySelector(`#header-${blockId} [data-id=${dataId}]`);

const openBurgerMenu = (burgerMenu, burgerButton) => {
  burgerMenu.classList.remove('burger-menu--hidden');
  burgerButton.classList.add('burger-menu--cross');
  if (isMobileScreen() && document.body) {
    document.body.style.overflow = 'hidden';
  }
};

const closeBurgerMenu = (burgerMenu, burgerButton) => {
  burgerMenu.classList.add('burger-menu--hidden');
  burgerButton.classList.remove('burger-menu--cross');
  if (isMobileScreen() && document.body) {
    document.body.style.overflow = 'visible';
  }
};

const attachBurgerMenuToggleHandler = (blockId, onOpenMenuCallback) => {
  const burgerMenu = getMountNodeByDataId(
    blockId,
    HeaderDataId.BURGER_MENU_LIST
  );
  const burgerButton = getMountNodeByDataId(
    blockId,
    HeaderDataId.BURGER_MENU_BUTTON
  );

  if (!burgerMenu || !burgerButton) {
    return;
  }

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeBurgerMenu(burgerMenu, burgerButton);
    }
  });

  document.addEventListener('click', (event: MouseEvent) => {
    const target: HTMLButtonElement = (event.target: any);
    if (
      // $FlowFixMe
      (!burgerMenu.parentElement.contains(target) && // $FlowFixMe
        !burgerMenu?.classList.contains('burger-menu--hidden')) ||
      target.closest(`[data-id=${HeaderDataId.USER_ACCOUNT_BUTTON}]`) ||
      target.closest(`[data-id=${HeaderDataId.CART_BUTTON}]`)
    ) {
      closeBurgerMenu(burgerMenu, burgerButton);
    }
  });

  burgerButton.addEventListener('click', () => {
    const isClosedMenu = burgerMenu.classList.contains('burger-menu--hidden');

    if (isClosedMenu) {
      openBurgerMenu(burgerMenu, burgerButton);
      onOpenMenuCallback();
    } else {
      closeBurgerMenu(burgerMenu, burgerButton);
    }
  });
};

const render = (component, blockId, dataId) => {
  const mountNode = getMountNodeByDataId(blockId, dataId);
  if (!mountNode) {
    return;
  }

  const element = (
    // quirk before we setup b2c localization in constructor
    <IntlProvider locale={defaultLocale}>{component}</IntlProvider>
  );
  ReactDOM.render(element, mountNode);
};

const renderHeaderBlock = ({
  blockId,
  headerBlock,
  cart,
  data,
  isLandingIncludeStoreBlock,
  layout,
  userService,
}) => {
  const mountNode = document.querySelector(`#header-${blockId} .block-wrapper`);
  if (!mountNode) {
    return;
  }

  const props = {
    id: blockId,
    block: headerBlock,
    widgets: data.widgets,
    skus: [],
    bundles: [],
    subscriptionPlans: [],
    isStoreDataLoading: false,
    isSubscriptionLoading: false,
    merchantId: data.merchantId,
    projectId: data.projectId,
    analyticsCounterId: data.analyticsCounterId,
    isNewScheme: data.isNewScheme,
    isMultipage: data.isMultipage,
    landingTheme: data.landingTheme,
    localeList: data.locales,
    locale: data.locale,
    auth: data.auth,
    renderMode: data.renderMode,
    isServerSideRender: true,
    isLandingIncludeStoreBlock,
    cartButtonOrderCount: cart.quantity,
    cartId: data.cartId,
    enableCart: data.cart.enable,
    layout,
    userService,
  };

  const headerReactElement = (
    <IntlProvider
      locale={defaultLocale} // quirk before we setup b2c localization in constructor
    >
      <Header {...props} />
    </IntlProvider>
  );
  mountNode.innerHTML = ReactDOMServer.renderToStaticMarkup(headerReactElement);
  lazyLoadImage.update();
};

const loadWithMultipage = async ({
  data,
  cart,
  storeAPIDataService,
  translations,
}) => {
  await Promise.all([cart.load(), storeAPIDataService.loadGameKeys()]);
  await initCartModalOnce({
    data,
    translations,
    dependencies: { cart, storeAPIDataService },
  });
  openCartModal({
    cart,
    storeAPIDataService,
    translations,
    isPreviewFrameMode: true,
  })();
};

export default ({
  block,
  blockId,
  blockStructure,
  data,
  translations,
}: ScriptCommonArgs) => {
  // flow type casting... :(
  const headerBlock: HeaderBlock = (blockStructure: any);

  const {
    cartId,
    loginId,
    projectId,
    merchantId,
    locale,
    locales,
    widgets,
    landingType,
    isMultipage,
    isNewScheme,
    auth,
    cart: cartSettings,
  } = data;

  const store = getStore();
  const { landingServices } = store.getState();
  const { userService } = landingServices;

  const userToken = getTokenFromCookie();
  const storeAPIDataService = StoreAPIDataService.getInstance(
    cartId,
    userToken,
    projectId,
    locale
  );
  const cart = GlobalUniversalCart.getInstance(
    projectId,
    cartId,
    storeAPIDataService,
    userToken
  );
  if (isMultipage) {
    loadWithMultipage({
      data,
      cart,
      storeAPIDataService,
      translations,
    });
  }

  const isLandingIncludeStoreBlock = !!data.blocksStructures.find(
    (block) => block.module === 'store'
  );
  const layout = HeaderLayout.compute({
    block: headerBlock,
    auth,
    widgets,
    localeList: locales,
    isMultipage,
    isLandingIncludeStoreBlock,
    userService,
    isNewScheme,
    enableCart: cartSettings.enable,
  });

  /**
   * 1. Static render of main Header block
   * 2. Setup manual handlers on static DOM nodes
   */
  renderHeaderBlock({
    blockId,
    headerBlock,
    cart,
    data,
    translations,
    isLandingIncludeStoreBlock,
    layout,
    userService,
  });
  initBuyButtonsInBlock({
    analyticsCategory: AnalyticsCategories.HEADER,
    block,
    data,
    translations,
  });
  if (layout !== LayoutEnum.LINE) {
    attachBurgerMenuToggleHandler(blockId, () =>
      sendClick(merchantId, XAEvents.openBurgerMenu)
    );
  }

  /**
   * Dynamic render and setup handlers and callbacks for CartButton
   */
  const renderCart = () => {
    render(
      <CartButton
        onClick={handleOpenCart(cart)}
        count={cart.quantity}
        locale={locale}
        hideText={layout !== LayoutEnum.LINE}
      />,
      blockId,
      HeaderDataId.CART_BUTTON
    );
  };
  if (data.isNewScheme) {
    cart.addListener(renderCart);
    renderCart();
  }

  /**
   * 1. Select position and create another element container (if it's necessary)
   * 2. Dynamic render and setup handlers and callbacks for UserAccount
  //  */
  const isShowUserAccountInLineHeader =
    HeaderLayout.isShowUserAccountInLineHeader({
      block: headerBlock,
      auth,
      widgets,
      localeList: locales,
      isMultipage,
      isLandingIncludeStoreBlock,
      landingType,
      userService,
    });

  const onLoginClick = () => {
    analyticsManager.sendEvent({
      category: AnalyticsCategories.HEADER,
      event: AnalyticsActions.CLICK_LOGIN,
      page: `landing_header_${AnalyticsActions.CLICK_LOGIN}-open`,
    });
    if (auth.type === AuthorizationType.LOGIN && loginId) {
      analyticsManager.sendEvent({
        category: AnalyticsCategories.HEADER,
        event: AnalyticsActions.OPEN_LOGIN,
        page: `landing_${AnalyticsActions.OPEN_LOGIN}`,
      });
      openLogin({
        source: LOGIN_SOURCES.HEADER,
        isCustomAuth: isCustomAuth(),
      });
    }
    const modal = document.querySelector(`[data-id=${userIdModalDataid}]`);
    if (auth.type !== AuthorizationType.LOGIN) {
      openModalWindow(modal, 'UserId');
      setLastLoginSource(LOGIN_SOURCES.HEADER);
      analyticsManager.sendEvent({
        category: AnalyticsCategories.HEADER,
        event: AnalyticsActions.OPEN_UID_WIDGET,
        page: `landing_${AnalyticsActions.OPEN_UID_WIDGET}`,
      });
    }
  };

  const renderUserAccount = () => {
    render(
      <UserAccount
        layout={
          layout === LayoutEnum.LINE || isShowUserAccountInLineHeader
            ? LayoutEnum.LINE
            : LayoutEnum.BURGER
        }
        locale={locale}
        onLoginClick={onLoginClick}
        merchantId={merchantId}
        userService={userService}
        isExperimentalFeature={isCustomAuth()}
      />,
      blockId,
      HeaderDataId.USER_ACCOUNT_BUTTON
    );
  };
  if (isUserAccountShown(auth)) {
    if (isShowUserAccountInLineHeader) {
      const oldContainer = getMountNodeByDataId(
        blockId,
        HeaderDataId.USER_ACCOUNT_BUTTON
      );
      const burgerMenu = getMountNodeByDataId(
        blockId,
        HeaderDataId.BURGER_MENU_LIST
      );
      if (oldContainer && burgerMenu) {
        // $FlowFixMe
        oldContainer.parentElement.style.display = 'none'; // removes burger menu section (wrapper that kept in himself UserAccount container)
        // $FlowFixMe
        burgerMenu.querySelector('hr').style.display = 'none'; // removes first hr line (that separate UserAccount and Custom Buttons)
      }
      const newContainer = document.createElement('div');
      newContainer.setAttribute('data-id', HeaderDataId.USER_ACCOUNT_BUTTON);
      newContainer.classList.add(
        'header__user-account',
        `header-${layout}__user-account`
      );
      const sideMenu = getMountNodeByDataId(blockId, HeaderDataId.SIDE_BLOCK);
      if (sideMenu) {
        sideMenu.prepend(newContainer);
      }
    }
    userService.addListener(renderUserAccount);
    renderUserAccount();
  }

  /**
   * Dynamic render and setup handlers for LocaleSelect
   */
  const handleLocaleSelectClick = (isOpenEvent: boolean) => {
    const burgerMenu = getMountNodeByDataId(
      blockId,
      HeaderDataId.BURGER_MENU_LIST
    );
    if (burgerMenu && isOpenEvent) {
      // smooth scroll after render burger menu
      setTimeout(() => {
        // $FlowFixMe
        burgerMenu.scrollTo({
          top: burgerMenu.clientHeight + burgerMenu.offsetTop,
          behavior: 'smooth',
        });
      }, 125);
    }
  };
  render(
    <LocaleSelect
      localeList={locales}
      currentLocale={locale}
      onClick={handleLocaleSelectClick}
      merchantId={merchantId}
      renderMode={RenderMode.PREVIEW}
      type={LocaleSelectType.SLIM}
      optionsAlign={layout === LayoutEnum.LINE ? 'right' : null}
      isExpandingOptionsList={layout === LayoutEnum.BURGER}
    />,
    blockId,
    HeaderDataId.LOCALE_SELECT
  );

  /**
   * Send custom events to push possible listeners
   */
  const event = new CustomEvent(events.header.init, {
    detail: { block, blockId, blockStructure },
  });
  window.dispatchEvent(event);
};
